import { useEffect, useRef, useState } from 'react';
import { Formik, Form } from 'formik';
import { useTranslation } from '@klv/hooks/useTranslation';
import Reaptcha from 'reaptcha';
import * as Yup from 'yup';

import { withOptimizely } from '@optimizely/react-sdk';
import { getStoredTrackingParams } from '@klv/shared/utils/attribution';
import {
  getUrlTrackingParams, submitForm, handleFieldBlur,
  windowIsDefined,
} from '@klv/shared/utils/functions';
import { debouncedBeginEvent, gtmFormEvent, combineGclidAndGaIdValue } from '@klv/shared/utils/google-tag-manager';
import { readLeadSourcePropertiesFromSessionStorage } from '@klv/shared/utils/leadSource';
import { hasStorageAvailable } from '@klv/shared/utils/storage';

import Button from '@klv/atoms/button';
import FormText from '@klv/atoms/form-text';
import CheckboxInput from '@klv/molecules/form-field/checkbox';
import HiddenInput from '@klv/molecules/form-field/hidden';
import PasswordInput from '@klv/molecules/form-field/password';
import TextInput from '@klv/molecules/form-field/text';

import CTALegalLanguage from '../cta-legal-language';
import AutofillSync from '../shared/autofill-sync';
import handleErrors from '../shared/handle-errors';
import handleSuccess from '../shared/handle-success';
import { getNumMarketingFields, ScrollToError } from '../utils';
import * as styles from '../styles/forms.module.scss';

import type { FocusEvent, FormEvent } from 'react';
import type { WithOptimizelyProps } from '@optimizely/react-sdk';
import type { AxiosError } from 'axios';
import type { IFormValidationSchema, IInitialFormValues, ISignupForm } from '../types';

const SimpleSignupForm = ({
  backgroundColor = 'dark',
  buttonPlacementDecision = false,
  formState,
  optimizely,
  optimizelyReadyTimeout,
  isServerSide,
  setFormState,
  setSuccess,
  ...props
}: ISignupForm & WithOptimizelyProps) => {
  const { t } = useTranslation();
  const urlTrackingParams = getUrlTrackingParams();
  const storedTrackingParams = getStoredTrackingParams();
  const recaptchaInstance = useRef<Reaptcha>(null);
  const errorElement = useRef<HTMLDivElement>(null);
  const [errors, setErrors] = useState<string[]>([]);

  const locale = t('langCodes_pathPrefix') || '';
  const fallbackURL = locale === '' ? '/solutions/ai/marketing-agent' : `/${locale}/solutions/ai/marketing-agent`;

  const getInitialValues = (() => {
    const emptyValues = {
      email: '',
      company_website: '',
      company: '',
      phone: '',
    };

    if (hasStorageAvailable('sessionStorage')) {
      const storedValues = sessionStorage.getItem('formValues');
      if (storedValues) {
        const parsed = JSON.parse(storedValues);

        return {
          ...emptyValues,
          ...parsed,
        };
      }
    }
    return emptyValues;
  })();

  useEffect(() => {
    debouncedBeginEvent('signup', 'marketing_agent');
    optimizely?.track?.('signup begin');

    if (!getInitialValues?.company_website && !formState?.company_website) {
      if (windowIsDefined()) {
        window.location.assign(fallbackURL);
      }
    }
  }, []);

  const storedLeadSourceProperties = readLeadSourcePropertiesFromSessionStorage();

  const initialValues: IInitialFormValues = {
    email: getInitialValues?.email,
    password1: '',
    first_page: `https://klaviyo.com${fallbackURL}`,
    next: '/account-setup/marketing-agent',
    company: getInitialValues?.company || getInitialValues?.company_website || formState?.company_website || '',
    company_website: getInitialValues?.company_website || formState?.company_website || '',
    phone: getInitialValues?.phone || '999-999-9999',
    field_1: Boolean(formState?.field_1) || false,
    'g-recaptcha-response': '',
    agreed_terms_of_service_and_privacy_policy: true,
    utm_source: urlTrackingParams?.utm_source || storedTrackingParams?.utm_source || '',
    utm_medium: urlTrackingParams?.utm_medium || storedTrackingParams?.utm_medium || '',
    utm_campaign: urlTrackingParams?.utm_campaign || storedTrackingParams?.utm_campaign || '',
    utm_content: urlTrackingParams?.utm_content || storedTrackingParams?.utm_content || '',
    utm_term: urlTrackingParams?.utm_term || storedTrackingParams?.utm_term || '',
    gclid: '',
    detail: urlTrackingParams?.detail || storedTrackingParams?.detail || '',
    wbraid: urlTrackingParams?.wbraid || storedTrackingParams?.wbraid || '',
    lead_source: urlTrackingParams?.lead_source || storedLeadSourceProperties?.lead_source || '',
    lead_source_detail: urlTrackingParams?.lead_source_detail || storedLeadSourceProperties?.lead_source_detail || '',
    num_marketing_fields: 16,
    field_6: formState?.field_6 || '',
    field_9: formState?.field_9 || '',
    field_10: formState?.field_10 || false,
    field_14: formState?.field_14 || '',
    field_16: formState?.field_16 || '',
    preferred_language:  t('langCode_languageString'),
    preferred_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, 
  };
  const initialValuesFields = Object.keys(initialValues).filter((fieldName) => fieldName.startsWith('field_'));
  const formStateAdditionalFields = Object.keys(formState).filter((fieldName) => fieldName.startsWith('field_')).filter((fieldName) => !initialValuesFields.includes(fieldName));

  if (formStateAdditionalFields?.length > 0) {
    formStateAdditionalFields.forEach((field) => {
      initialValues[field] = formState?.[field] || '';
    });
  }

  // Password validation criteria from https://github.com/klaviyo/app/blob/master/src/learning/app/auth/validators.py:
  // - Not blank
  // - At least 12 characters
  // - Not more than 128 characters
  // - Cannot contain "klaviyo"
  // - Cannot be in the list of 10,000 most common passwords (we defer to the server for this)
  const validationSchemaYupObject: IFormValidationSchema = {
    email: Yup.string()
      .email(t('organisms_forms_fields_email_errors_email') || 'Please make sure your email address includes a single @, followed by a valid domain (name@example.com)')
      .required(t('organisms_forms_fields_email_errors_required') || 'Required'),
    password1: Yup.string()
      .required(t('organisms_forms_fields_password_errors_required') || 'Required')
      .min(12, t('organisms_forms_fields_password_errors_min') || 'Please make sure your password includes at least 12 characters.')
      .max(128, t('organisms_forms_fields_password_errors_max') || 'Please make sure your password is not more than 128 characters long.')
      .matches(/^((?!klaviyo).)*$/i, t('organisms_forms_fields_password_errors_common') || 'Your password is too common'), // Matching the error text from the app
    company: Yup.string(),
    company_website: Yup.string(),
    phone: Yup.string(),
    preferred_language: Yup.string(),
    preferred_timezone: Yup.string(),
    field_1: Yup.boolean(),
    next: Yup.string(),
    'g-recaptcha-response': Yup.string(),
    agreed_terms_of_service_and_privacy_policy: Yup.boolean(),
    utm_source: Yup.string(),
    utm_medium: Yup.string(),
    utm_campaign: Yup.string(),
    utm_content: Yup.string(),
    utm_term: Yup.string(),
    gclid: Yup.string(),
    detail: Yup.string(),
    wbraid: Yup.string(),
    first_page: Yup.string(),
    lead_source: Yup.string(),
    lead_source_detail: Yup.string(),
    num_marketing_fields: Yup.string(),
    field_6: Yup.string(),
    field_9: Yup.string(),
    field_10: Yup.boolean(),
    field_14: Yup.string(),
    field_16: Yup.string(),
  };

  // create a reset function
  const resetRecaptcha = () => {
    recaptchaInstance.current?.reset();
  };

  return (
    <div data-testid="simple-signup-form" {...props}>
      {errors?.length > 0 && (
        <div className={styles.errorContainer}>
          {errors.map((error: string) => (
            <ScrollToError
            key={error}
            innerRef={errorElement}>
              <FormText className={styles.formError} dangerouslySetInnerHTML={{ __html: error }} />
            </ScrollToError>
          ))}
        </div>)}
      <Formik
        initialValues={initialValues}
        validationSchema={Yup.object(validationSchemaYupObject)}
        onSubmit={async (values, formikBag) => {
          const googleClientId = await combineGclidAndGaIdValue(urlTrackingParams?.gclid);
          const irclickid = urlTrackingParams?.irclickid || storedTrackingParams?.irclickid || '';
          values.gclid = `${googleClientId}${irclickid ? `:${irclickid}` : ''}`;
          values.num_marketing_fields = getNumMarketingFields(values);

          if (values['g-recaptcha-response'] === '') {
            setErrors([t('organisms_forms_errors_recaptcha') || 'reCaptcha is required']);

            return false;
          }
          try {
            const { setFieldError } = formikBag;
            const urlBase = (process.env.NODE_ENV === 'production') ? 'https://www.klaviyo.com' : '';
            const response = await submitForm(`${urlBase}/ajax/register`, values);
            if (response?.status !== 200) {
              setErrors([t('organisms_forms_errors_network') || 'Your request was not successful because of a network error. Please try again.']);
            }
            if (response?.data?.success) {
              setErrors([]);
              setSuccess(true);
              handleSuccess(values, 'signup');
              optimizely?.track?.('signup complete', undefined, { ...optimizely?.user?.attributes, formComplete: true });
              if (windowIsDefined() && response?.data?.data?.next) {
                gtmFormEvent(values, 'signup', 'complete', 'marketing_agent').then(() => {
                  window.location =  response.data.data.next;
                });
              }

              return response;
            }

            handleErrors(response?.data?.errors || [], setErrors, setFieldError, values, 'signup');
            resetRecaptcha();

            return false;
          } catch (error) {
            const knownError = error as AxiosError;
            window?.heap?.track?.('Form Submission Error', {
              code: knownError?.code ?? '',
              emailAddress: values?.email ?? '',
              formName: 'signup',
              host: process.env.GATSBY_HOST ?? '',
              message: knownError?.message ?? '',
              name: knownError?.name ?? '',
              rawJson: JSON.stringify(error) ?? '',
              stack: knownError?.stack ?? '',
              statusCode: knownError?.status ?? '',
            });
            if (knownError?.response?.status === 429) {
              setErrors([knownError?.response?.data as string || t('organisms_forms_errors_rateLimit')]);
            } else {
              setErrors([t('organisms_forms_errors_generic') || 'Your request was not successful. Please try again.']);
            }
            resetRecaptcha();

            return false;
          }
        }}
      >
        
        {(formik) => (
          <Form id="form--signup">
            <TextInput
              backgroundColor={backgroundColor}
              id="signupForm-email"
              label={t('organisms_forms_fields_email_label')}
              name="email"
              type="email"
              placeholder={t('organisms_forms_fields_email_placeholder') || 'name@example.com'}
              onBlur={(event: FocusEvent) => handleFieldBlur(event, formik, setFormState, 'signup')}
              autofocus
            />

            <PasswordInput
              backgroundColor={backgroundColor}
              id="signupForm-password1"
              label={t('organisms_forms_fields_password_label')}
              name="password1"
              placeholder={t('organisms_forms_fields_password_placeholder') || 'Enter your password'}
              autocomplete='new-password'
              showPasswordStrength={true}
              onBlur={(event: FocusEvent) => handleFieldBlur(event, formik, setFormState, 'signup')}
            />

            {buttonPlacementDecision && 
              <div  style={{ marginBottom: '20px' }}>
                <Button text={formik.isSubmitting ? t('organisms_forms_signup_buttonSubmitting') || 'Submitting...' : t('organisms_forms_signup_button') || 'Create account'} buttonColor={backgroundColor === 'dark' ? 'light' : 'dark'} variant="primary" type="submit" /> 
              </div>
            }
            <div className={styles.marginBottom25}>
              <CheckboxInput 
                backgroundColor={backgroundColor}
                id="signupForm-newsletter"
                label={t('organisms_forms_fields_newsletter_label') || 'Tell me more. Sign me up for marketing communications from Klaviyo.*'}
                name="field_1"
                size="small"
                onChange={(event: FormEvent) => handleFieldBlur(event, formik, setFormState, 'signup')}
              />
            </div>

            <div className={styles.marginBottom25}>
              <Reaptcha
                hl={t('langCodes_recaptchaLanguage')}
                ref={recaptchaInstance}
                sitekey={process.env.GATSBY_RECAPTCHA_KEY}
                onVerify={(response) => {
                  formik.setFieldValue('g-recaptcha-response', response);
                }}
                theme={'light'}
              />
            </div>

            <HiddenInput id="company-signup" name="company" />
            <HiddenInput id="company_website-signup" name="company_website" />
            <HiddenInput id="phone-signup" name="phone" />
        
            <HiddenInput id="agreed-terms-of-service-and-privacy-policy-signup" name="agreed_terms_of_service_and_privacy_policy" />
            <HiddenInput id="utm-source-signup" name="utm_source" />
            <HiddenInput id="utm-medium-signup" name="utm_medium" />
            <HiddenInput id="utm-campaign-signup" name="utm_campaign" />
            <HiddenInput id="utm-content-signup" name="utm_content" />
            <HiddenInput id="utm-term-signup" name="utm_term" />
            <HiddenInput id="gclid-signup" name="gclid" />
            <HiddenInput id="detail-signup" name="detail" />
            <HiddenInput id="wbraid-signup" name="wbraid" />
            <HiddenInput id="first-page-signup" name="first_page" />
            <HiddenInput id="lead-source-signup" name="lead_source" />
            <HiddenInput id="lead-source-detail-signup" name="lead_source_detail" />
            <HiddenInput id="num-marketing-fields-signup" name="num_marketing_fields" />
            <HiddenInput id="field-6-signup" name="field_6" />
            <HiddenInput id="field-9-signup" name="field_9" />
            <HiddenInput id="field-14-signup" name="field_14" />
            <HiddenInput id="field-10-signup" name="field_10" />
            {formStateAdditionalFields?.length > 0 && formStateAdditionalFields.map((field) => (
              <HiddenInput id={`${field.replace('_', '-')}-signup`} name={field} key={field} />
            ))}

            {!buttonPlacementDecision && 
              <Button text={formik.isSubmitting ? t('organisms_forms_signup_buttonSubmitting') || 'Submitting...' : t('organisms_forms_signup_button') || 'Create account'} buttonColor={backgroundColor === 'dark' ? 'light' : 'dark'} variant="primary" type="submit" /> 
            }
            <CTALegalLanguage colorVariant={backgroundColor === 'dark' ? 'light' : 'dark'} formType="signup" />
            <AutofillSync formik={formik} fields={['email', 'password1']} />
          </Form>
        )}
      </Formik>

    </div>
  );
};


export default withOptimizely(SimpleSignupForm);
